import { useCallback, useEffect, useState } from 'react';
import { api, fmt } from '../api';

type Status = Awaited<ReturnType<typeof api.shopifyPixelStatus>>;

export default function PixelSetup() {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);

  const refresh = useCallback(() => {
    api.shopifyPixelStatus().then(setStatus).catch((e) => {
      setError(e instanceof Error ? e.message : 'Could not check tracking status.');
    });
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 10_000);
    return () => clearInterval(timer);
  }, [refresh]);

  async function install() {
    setInstalling(true);
    setError(null);
    try {
      await api.installShopifyPixel();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Pixel installation failed.');
    } finally {
      setInstalling(false);
    }
  }

  return (
    <>
      <p className="eyebrow">Setup · tracking</p>
      <h1 className="pg-title">Shopify tracking</h1>
      <p className="pg-sub">
        ScaleAds AI installs a consent-aware storefront script automatically. Shopify order webhooks
        provide the authoritative purchase value; browser events provide the attribution trail used
        for Meta CAPI when the visitor has granted the required consent.
      </p>

      {error && <p className="warn-line">{error}</p>}
      {!status && !error && <div className="empty">Checking tracking…</div>}
      {status && (
        <>
          <div className={`card card-p setup-status ${status.scriptInstalled ? 'done' : ''}`}>
            <h4>Storefront installation</h4>
            <p className={status.scriptInstalled ? 'ok-line' : 'warn-line'}>
              {status.scriptInstalled
                ? 'Installed on the Online Store and governed by Shopify Customer Privacy consent.'
                : 'Not installed. Install it before sending traffic.'}
            </p>
            {!status.scriptInstalled && (
              <button className="btn brand" disabled={installing} onClick={() => void install()}>
                {installing ? 'Installing…' : 'Install tracking'}
              </button>
            )}
          </div>

          <div className="grid-4">
            <div className="stat"><span>Page views (24h)</span><strong>{fmt.num(status.pageViews24h)}</strong></div>
            <div className="stat"><span>Add to carts (24h)</span><strong>{fmt.num(status.addToCarts24h)}</strong></div>
            <div className="stat"><span>Pixel purchases (24h)</span><strong>{fmt.num(status.purchases24h)}</strong></div>
            <div className="stat"><span>Shopify orders (24h)</span><strong>{fmt.num(status.orderSignals24h)}</strong></div>
          </div>

          <div className="card card-p">
            <h4>Consent and delivery rules</h4>
            <ul>
              <li>No browser event is stored until analytics consent is granted.</li>
              <li>Meta CAPI delivery also requires marketing consent and permission to share data.</li>
              <li>Global Privacy Control disables advertising delivery.</li>
              <li>Campaign and ad identifiers are matched from UTM parameters; Shopify orders remain the revenue source of truth.</li>
            </ul>
          </div>
        </>
      )}
    </>
  );
}
